/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import PeopleTagger, { PeoplePills } from "../components/PeopleTagger";
import ReorderControls from "../components/ReorderControls";
import Pagination from "../components/Pagination";

export default function AllocationsPage() {
  const { selectedMatchId } = useMatch();
  const DEMO = selectedMatchId;
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customCats, setCustomCats] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ category: "Godown", location: "", allocated_to: "", purpose: "", tagged_people: [] });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  // Category chip filter mirrors Pre-Match Tracker UX — "all" or a specific category.
  const [filter, setFilter] = useState("all");
  // Per-category pagination state (20 per page, mirrors Pre-Match Tracker).
  const [pages, setPages] = useState({});
  const PER_PAGE = 20;
  const [orphans, setOrphans] = useState([]);
  const [orphanTarget, setOrphanTarget] = useState({}); // orphan category -> target category
  const [merging, setMerging] = useState("");

  const load = async () => {
    if (!DEMO) return;
    const [{ data: cats }, { data }, { data: orph }] = await Promise.all([
      api.get(`/reference/allocation-categories?match_id=${DEMO}`),
      api.get(`/matches/${DEMO}/allocations`),
      api.get(`/matches/${DEMO}/allocation-orphans`),
    ]);
    setCategories(cats.categories);
    setCustomCats(cats.custom || []);
    setRows(data);
    setOrphans(orph.orphans || []);
  };
  useEffect(() => { load(); }, [DEMO]);

  const mergeOrphan = async (fromCat) => {
    const to = orphanTarget[fromCat];
    if (!to) {
      alert("Pick a target category first.");
      return;
    }
    if (!window.confirm(`Move every allocation currently tagged "${fromCat}" into "${to}"? This updates the rows in place and cannot be undone from the UI.`)) return;
    setMerging(fromCat);
    try {
      const { data } = await api.post(`/matches/${DEMO}/allocation-orphans/merge`, {
        from_category: fromCat,
        to_category: to,
      });
      alert(`Moved ${data.moved} allocation${data.moved === 1 ? "" : "s"} from "${fromCat}" into "${to}".`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not merge orphans.");
    } finally {
      setMerging("");
    }
  };

  const addCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      await api.post(`/matches/${DEMO}/custom-allocation-categories`, { name });
      setNewCategoryName("");
      setAddingCategory(false);
      setForm((f) => ({ ...f, category: name }));
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not add category.");
    }
  };

  const removeCategory = async (name) => {
    const inUse = rows.filter((r) => r.category === name).length;
    const msg = inUse > 0
      ? `Remove "${name}"?\n\n${inUse} allocation${inUse > 1 ? "s" : ""} in this category will stay in the DB but no longer appear grouped.`
      : `Remove empty category "${name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/matches/${DEMO}/custom-allocation-categories/${encodeURIComponent(name)}`);
      if (form.category === name) setForm((f) => ({ ...f, category: categories.find((c) => c !== name) || "Godown" }));
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not remove category.");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    await api.post(`/matches/${DEMO}/allocations`, form);
    setForm({ category: form.category, location: "", allocated_to: "", purpose: "", tagged_people: [] });
    setShowNew(false);
    load();
  };

  const save = async (id) => {
    await api.patch(`/allocations/${id}`, editForm);
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this allocation?")) return;
    await api.delete(`/allocations/${id}`);
    load();
  };

  const move = async (category, idx, delta) => {
    const list = rows.filter((r) => r.category === category);
    const j = idx + delta;
    if (j < 0 || j >= list.length) return;
    const reordered = [...list];
    [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
    // Optimistic reorder
    const otherCats = rows.filter((r) => r.category !== category);
    setRows([...otherCats, ...reordered]);
    await api.post(`/matches/${DEMO}/allocations/reorder`, {
      category,
      ids: reordered.map((r) => r.id),
    });
    load();
  };

  if (!selectedMatchId) return <RequireMatch />;

  const sortedCategories = [...categories].sort((a, b) => a.localeCompare(b));
  const grouped = sortedCategories.reduce((acc, c) => ({ ...acc, [c]: rows.filter((r) => r.category === c) }), {});
  // Apply the chip filter — "all" shows every category block, a specific
  // category shows only that one. Count label mirrors Pre-Match Tracker.
  const visibleCategories = filter === "all" ? sortedCategories : sortedCategories.filter((c) => c === filter);
  const visibleRowCount = visibleCategories.reduce((n, c) => n + (grouped[c]?.length || 0), 0);
  const setPage = (cat, p) => setPages((prev) => ({ ...prev, [cat]: p }));
  const pageOf = (cat) => pages[cat] || 1;

  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-allocations">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Pre-Match · Space &amp; Resource Allocation</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Allocations</h1>
          <p className="text-zinc-400 mt-2 text-sm max-w-2xl">
            Record who has been allocated which godown, room, corporate box, parking spot, or other resource — grouped by category for quick reference.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {addingCategory ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                className="bg-black border border-cmdBorder p-2 text-sm w-48"
                placeholder="New category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCategory()}
                data-testid="alloc-new-cat-input"
              />
              <button onClick={addCategory} className="bg-white text-black px-3 py-2 text-xs font-bold uppercase tracking-widest" data-testid="alloc-new-cat-save">Add</button>
              <button onClick={() => { setAddingCategory(false); setNewCategoryName(""); }} className="border border-cmdBorder px-3 py-2 text-xs">×</button>
            </div>
          ) : (
            <button
              onClick={() => setAddingCategory(true)}
              className="flex items-center gap-1 border border-dashed border-cmdBorder text-zinc-400 hover:border-white hover:text-white px-3 py-2 text-xs uppercase tracking-widest transition-colors"
              data-testid="alloc-new-cat-btn"
            >
              <Plus className="w-3 h-3" /> Category
            </button>
          )}
          <button
            onClick={() => setShowNew((v) => !v)}
            className="bg-white text-black px-4 py-2 font-bold uppercase tracking-widest text-xs"
            data-testid="alloc-new-btn"
          >
            {showNew ? "Cancel" : "+ Add Allocation"}
          </button>
        </div>
      </div>

      {showNew && (
        <form onSubmit={create} className="mc-panel p-5 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="alloc-form">
          <select
            className="bg-black border border-cmdBorder p-2 text-sm"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            data-testid="alloc-form-category"
          >
            {sortedCategories.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input
            className="bg-black border border-cmdBorder p-2 text-sm"
            placeholder="Location (e.g., G-3 / Room 204)"
            required
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            data-testid="alloc-form-location"
          />
          <input
            className="bg-black border border-cmdBorder p-2 text-sm"
            placeholder="Allocated to"
            required
            value={form.allocated_to}
            onChange={(e) => setForm({ ...form, allocated_to: e.target.value })}
            data-testid="alloc-form-allocated"
          />
          <input
            className="bg-black border border-cmdBorder p-2 text-sm"
            placeholder="Purpose"
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
            data-testid="alloc-form-purpose"
          />
          <button
            type="submit"
            className="bg-readyGreen text-black font-bold uppercase tracking-widest text-xs"
            data-testid="alloc-form-submit"
          >
            Add
          </button>
          <div className="md:col-span-5">
            <label className="mc-label">Tag Individuals (optional)</label>
            <div className="mt-1">
              <PeopleTagger
                value={form.tagged_people}
                onChange={(v) => setForm({ ...form, tagged_people: v })}
                placeholder="Add key people responsible for this allocation…"
                testidPrefix="alloc-tagger"
              />
            </div>
          </div>
        </form>
      )}

      {orphans.length > 0 && (
        <div className="mc-panel border-l-4 border-incidentRed p-4 mb-6" data-testid="alloc-orphans-banner">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div>
              <div className="mc-label text-incidentRed">Hidden allocations detected</div>
              <p className="text-xs text-zinc-400 mt-1">
                {orphans.reduce((n, o) => n + o.count, 0)} allocation{orphans.reduce((n, o) => n + o.count, 0) === 1 ? "" : "s"} on this match are tagged with a category name that no longer exists (likely from an old rename). Pick a destination and merge to restore them.
              </p>
            </div>
          </div>
          <div className="space-y-2 mt-3">
            {orphans.map((o) => (
              <div key={o.category} className="flex items-center flex-wrap gap-2 border border-cmdBorder px-3 py-2" data-testid={`alloc-orphan-${o.category.replace(/\s+/g, "-")}`}>
                <div className="flex-1 min-w-[180px]">
                  <div className="text-sm font-bold">{o.category}</div>
                  <div className="text-[10px] text-zinc-500 font-mono-live">{o.count} hidden allocation{o.count === 1 ? "" : "s"}</div>
                </div>
                <select
                  className="bg-black border border-cmdBorder p-2 text-xs"
                  value={orphanTarget[o.category] || ""}
                  onChange={(e) => setOrphanTarget((m) => ({ ...m, [o.category]: e.target.value }))}
                  data-testid={`alloc-orphan-target-${o.category.replace(/\s+/g, "-")}`}
                >
                  <option value="">Merge into…</option>
                  {sortedCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                  onClick={() => mergeOrphan(o.category)}
                  disabled={merging === o.category || !orphanTarget[o.category]}
                  className="bg-readyGreen text-white font-bold uppercase tracking-widest text-xs px-3 py-2 disabled:opacity-50"
                  data-testid={`alloc-orphan-merge-${o.category.replace(/\s+/g, "-")}`}
                >
                  {merging === o.category ? "Merging…" : "Merge"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category filter chips — mirrors Pre-Match Tracker's chip row */}
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap" data-testid="alloc-filter-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFilter("all")}
            className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors ${
              filter === "all"
                ? "border-white bg-white text-black"
                : "border-cmdBorder text-zinc-400 hover:border-white hover:text-white"
            }`}
            data-testid="alloc-filter-all"
          >
            All
          </button>
          {sortedCategories.map((c) => {
            const n = grouped[c]?.length || 0;
            const active = filter === c;
            return (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors ${
                  active
                    ? "border-white bg-white text-black"
                    : "border-cmdBorder text-zinc-400 hover:border-white hover:text-white"
                }`}
                data-testid={`alloc-filter-${c.replace(/\s+/g, "-")}`}
              >
                {c}
                <span className={`ml-1.5 font-mono-live ${active ? "text-black/60" : "text-zinc-600"}`}>{n}</span>
              </button>
            );
          })}
        </div>
        <div className="font-mono-live text-xs text-zinc-500" data-testid="alloc-count">
          {visibleRowCount} of {rows.length} allocations
        </div>
      </div>

      <div className="space-y-6">
        {visibleCategories.map((cat) => {
          const items = grouped[cat] || [];
          const page = pageOf(cat);
          const pageItems = items.slice((page - 1) * PER_PAGE, page * PER_PAGE);
          return (
            <div key={cat} className="mc-panel" data-testid={`alloc-cat-${cat.replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-cmdBorder">
                <div>
                  <div className="mc-label">Category</div>
                  <h2 className="font-head font-bold text-xl uppercase mt-0.5">{cat}</h2>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono-live text-zinc-500">{items.length} allocation{items.length === 1 ? "" : "s"}</span>
                  <button
                    onClick={async () => {
                      const nn = window.prompt(`Rename "${cat}" to:`, cat);
                      if (!nn || nn.trim() === cat) return;
                      try {
                        await api.patch(`/matches/${DEMO}/custom-allocation-categories/${encodeURIComponent(cat)}`, { new_name: nn.trim() });
                        if (form.category === cat) setForm((f) => ({ ...f, category: nn.trim() }));
                        await load();
                      } catch (e) {
                        alert(e.response?.data?.detail || "Could not rename category.");
                      }
                    }}
                    className="border border-cmdBorder hover:border-white px-2 py-1 text-xs transition-colors"
                    data-testid={`alloc-cat-edit-${cat.replace(/\s+/g, "-")}`}
                    title="Rename this category"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => removeCategory(cat)}
                    className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-1 text-xs transition-colors"
                    data-testid={`alloc-cat-del-${cat.replace(/\s+/g, "-")}`}
                    title="Delete this category"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              {items.length === 0 ? (
                <div className="text-center text-zinc-500 py-6 text-sm">No allocations in this category.</div>
              ) : (
                <>
                <table className="w-full mc-table hidden md:table">
                  <thead>
                    <tr>
                      <th style={{ width: 56 }}></th>
                      <th>Location</th>
                      <th>Allocated To</th>
                      <th>Purpose</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((r, idx) => (
                      <tr key={r.id} data-testid={`alloc-row-${r.id}`}>
                        {editing === r.id ? (
                          <>
                            <td></td>
                            <td>
                              <input
                                className="bg-black border border-cmdBorder p-1.5 text-xs w-full"
                                value={editForm.location ?? ""}
                                onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                                data-testid={`alloc-edit-location-${r.id}`}
                              />
                            </td>
                            <td>
                              <input
                                className="bg-black border border-cmdBorder p-1.5 text-xs w-full"
                                value={editForm.allocated_to ?? ""}
                                onChange={(e) => setEditForm({ ...editForm, allocated_to: e.target.value })}
                                data-testid={`alloc-edit-allocated-${r.id}`}
                              />
                            </td>
                            <td>
                              <input
                                className="bg-black border border-cmdBorder p-1.5 text-xs w-full"
                                value={editForm.purpose ?? ""}
                                onChange={(e) => setEditForm({ ...editForm, purpose: e.target.value })}
                                data-testid={`alloc-edit-purpose-${r.id}`}
                              />
                            </td>
                            <td className="text-right">
                              <div className="inline-flex gap-2 justify-end">
                                <button
                                  onClick={() => save(r.id)}
                                  className="border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white px-2 py-1 text-xs"
                                  data-testid={`alloc-save-${r.id}`}
                                >
                                  <Check className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => setEditing(null)}
                                  className="border border-cmdBorder hover:border-white px-2 py-1 text-xs"
                                  data-testid={`alloc-cancel-${r.id}`}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>
                              <ReorderControls
                                onUp={() => move(cat, idx, -1)}
                                onDown={() => move(cat, idx, 1)}
                                canUp={idx > 0}
                                canDown={idx < items.length - 1}
                                testidPrefix={`alloc-reorder-${r.id}`}
                              />
                            </td>
                            <td className="font-medium">{r.location}</td>
                            <td className="text-zinc-300">
                              {r.allocated_to}
                              {r.tagged_people?.length > 0 && (
                                <div className="mt-1"><PeoplePills people={r.tagged_people} testidPrefix={`alloc-tagged-${r.id}`} /></div>
                              )}
                            </td>
                            <td className="text-zinc-400 text-xs">{r.purpose || <span className="text-zinc-600">—</span>}</td>
                            <td className="text-right">
                              <div className="inline-flex gap-2 justify-end">
                                <button
                                  onClick={() => { setEditing(r.id); setEditForm({ location: r.location, allocated_to: r.allocated_to, purpose: r.purpose, category: r.category }); }}
                                  className="border border-cmdBorder hover:border-white px-2 py-1 text-xs"
                                  data-testid={`alloc-edit-${r.id}`}
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => remove(r.id)}
                                  className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-1 text-xs"
                                  data-testid={`alloc-del-${r.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination below desktop table — appears when > 20 rows */}
                <div className="px-4 pb-3 hidden md:block">
                  <Pagination
                    page={page}
                    perPage={PER_PAGE}
                    total={items.length}
                    onPageChange={(p) => setPage(cat, p)}
                    testidPrefix={`alloc-pagination-${cat.replace(/\s+/g, "-")}`}
                  />
                </div>
                {/* Mobile: card list — replaces the table on small screens */}
                <div className="md:hidden divide-y divide-cmdBorder" data-testid={`alloc-mobile-${cat.replace(/\s+/g, "-")}`}>
                  {pageItems.map((r, idx) => (
                    <div key={r.id} className="p-4" data-testid={`alloc-card-${r.id}`}>
                      {editing === r.id ? (
                        <div className="space-y-2">
                          <input
                            className="w-full bg-black border border-cmdBorder p-2 text-sm"
                            placeholder="Location"
                            value={editForm.location ?? ""}
                            onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                            data-testid={`alloc-edit-location-m-${r.id}`}
                          />
                          <input
                            className="w-full bg-black border border-cmdBorder p-2 text-sm"
                            placeholder="Allocated to"
                            value={editForm.allocated_to ?? ""}
                            onChange={(e) => setEditForm({ ...editForm, allocated_to: e.target.value })}
                            data-testid={`alloc-edit-allocated-m-${r.id}`}
                          />
                          <input
                            className="w-full bg-black border border-cmdBorder p-2 text-sm"
                            placeholder="Purpose"
                            value={editForm.purpose ?? ""}
                            onChange={(e) => setEditForm({ ...editForm, purpose: e.target.value })}
                            data-testid={`alloc-edit-purpose-m-${r.id}`}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => save(r.id)}
                              className="flex-1 flex items-center justify-center gap-1 border border-readyGreen text-readyGreen h-10 text-xs uppercase tracking-widest"
                              data-testid={`alloc-save-m-${r.id}`}
                            >
                              <Check className="w-4 h-4" /> Save
                            </button>
                            <button
                              onClick={() => setEditing(null)}
                              className="border border-cmdBorder h-10 w-10 flex items-center justify-center"
                              data-testid={`alloc-cancel-m-${r.id}`}
                              aria-label="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-mono-live text-[10px] text-zinc-500 uppercase tracking-widest">Location</div>
                              <div className="font-medium text-sm">{r.location}</div>
                              <div className="font-mono-live text-[10px] text-zinc-500 uppercase tracking-widest mt-2">Allocated to</div>
                              <div className="text-zinc-300 text-sm">{r.allocated_to}</div>
                              {r.purpose && (
                                <>
                                  <div className="font-mono-live text-[10px] text-zinc-500 uppercase tracking-widest mt-2">Purpose</div>
                                  <div className="text-zinc-400 text-xs">{r.purpose}</div>
                                </>
                              )}
                              {r.tagged_people?.length > 0 && (
                                <div className="mt-2"><PeoplePills people={r.tagged_people} testidPrefix={`alloc-tagged-m-${r.id}`} /></div>
                              )}
                            </div>
                            <ReorderControls
                              onUp={() => move(cat, idx, -1)}
                              onDown={() => move(cat, idx, 1)}
                              canUp={idx > 0}
                              canDown={idx < items.length - 1}
                              testidPrefix={`alloc-reorder-m-${r.id}`}
                            />
                          </div>
                          <div className="mt-3 flex gap-2">
                            <button
                              onClick={() => { setEditing(r.id); setEditForm({ location: r.location, allocated_to: r.allocated_to, purpose: r.purpose, category: r.category }); }}
                              className="flex-1 flex items-center justify-center gap-1 border border-cmdBorder hover:border-white h-10 text-xs uppercase tracking-widest"
                              data-testid={`alloc-edit-m-${r.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button
                              onClick={() => remove(r.id)}
                              className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed h-10 w-10 flex items-center justify-center"
                              data-testid={`alloc-del-m-${r.id}`}
                              aria-label="Delete allocation"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  <div className="p-4">
                    <Pagination
                      page={page}
                      perPage={PER_PAGE}
                      total={items.length}
                      onPageChange={(p) => setPage(cat, p)}
                      testidPrefix={`alloc-pagination-mobile-${cat.replace(/\s+/g, "-")}`}
                    />
                  </div>
                </div>
                </>
              )}
            </div>
          );
        })}
        {visibleCategories.length === 0 && (
          <div className="mc-panel p-10 text-center text-zinc-500 text-sm" data-testid="alloc-empty-filter">
            No category matches the current filter.
          </div>
        )}
      </div>
    </div>
  );
}
